import { computed } from 'vue';
import { useStore } from '../store';

/**
 * Composable for accessing Flipper feature flags from the current account
 * @returns {Object} Object containing:
 *   - isEnabled(flagName): ComputedRef<boolean> - Check if a flag is enabled
 *   - flags: ComputedRef<Object> - All enabled flags
 * @example
 * const { isEnabled, flags } = useFlipper();
 *
 * // Check single flag
 * if (isEnabled('reply_to_message').value) {
 *   // feature is enabled
 * }
 *
 * // Get all flags
 * console.log(flags.value);
 * // => { tiktok_integration: true, overview_v2: true }
 *
 * @see {@link ./README.md} For complete documentation and usage patterns
 */
export const useFlipper = () => {
  const store = useStore();

  /**
   * Current account's flipper features (reactive)
   */
  const flags = computed(() => {
    const currentAccountId = store.getters.getCurrentAccountId;
    if (!currentAccountId) return {};

    const account = store.getters['accounts/getAccount'](currentAccountId);
    return account?.flipper_features || {};
  });

  /**
   * Check if a Flipper feature flag is enabled for the current account
   * @param {string} flagName - The name of the feature flag
   * @returns {ComputedRef<boolean>} Computed ref that returns true if flag is enabled
   */
  const isEnabled = flagName => {
    return computed(() => flags.value[flagName] || false);
  };

  return {
    isEnabled,
    flags,
  };
};
